import React, { useState, useEffect, useCallback } from 'react';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useParticipants,
  useTracks,
  useDataChannel,
  useRoomContext,
} from '@livekit/components-react';
import { Track, DataPacket_Kind, Room, RoomEvent, ConnectionState } from 'livekit-client';
import { Mic, MicOff, Send, Phone, PhoneOff, Users, MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

interface LiveKitChatRoomProps {
  roomName: string;
  token: string;
  wsUrl: string;
  onDisconnect?: () => void;
  enableVoice?: boolean;
  enableVideo?: boolean;
  className?: string;
}

interface ChatMessage {
  id: string;
  userId: string;
  userName: string;
  message: string;
  timestamp: Date;
  type: 'user' | 'system';
}

const ChatInterface: React.FC<{
  room: Room | undefined;
  onSendMessage: (message: string) => void;
  messages: ChatMessage[];
}> = ({ room, onSendMessage, messages }) => {
  const [messageText, setMessageText] = useState('');
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (room) {
      setIsConnected(room.state === ConnectionState.Connected);
      
      const handleStateChange = () => {
        setIsConnected(room.state === ConnectionState.Connected);
      };
      
      room.on('connectionStateChanged', handleStateChange);
      return () => {
        room.off('connectionStateChanged', handleStateChange);
      };
    }
  }, [room]);

  const handleSendMessage = () => {
    if (messageText.trim() && isConnected) {
      onSendMessage(messageText.trim());
      setMessageText('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <MessageSquare className="h-5 w-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Live Chat</h3>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="text-sm text-gray-600">
            {isConnected ? 'Connected' : 'Connecting...'}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  message.type === 'user'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-900'
                }`}
              >
                <div className="text-sm font-medium mb-1">
                  {message.userName}
                </div>
                <div className="text-sm">{message.message}</div>
                <div className="text-xs mt-1 opacity-75">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Message Input */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex space-x-2">
          <input
            type="text"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            disabled={!isConnected}
          />
          <button
            onClick={handleSendMessage}
            disabled={!messageText.trim() || !isConnected}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Binds to LiveKit Room provided by LiveKitRoom via context and forwards the
// fully-initialized Room instance to our handler when available
const RoomBinder: React.FC<{ onConnected: (room: Room) => void }> = ({ onConnected }) => {
  const room = useRoomContext();

  useEffect(() => {
    if (!room) return;

    const maybeReport = () => {
      if (room.state === ConnectionState.Connected) {
        onConnected(room);
      }
    };

    // Call immediately if already connected
    maybeReport();

    // Also listen for state changes to catch the moment it becomes connected
    room.on('connectionStateChanged', maybeReport);
    return () => {
      room.off('connectionStateChanged', maybeReport);
    };
  }, [room, onConnected]);

  return null;
};

// Watches the internal Room connection state and reports it upward so the UI
// indicator matches the real SDK state even if the room instance isn't passed
// via props.
const ConnectionWatcher: React.FC<{ onStateChange: (state: ConnectionState) => void, onRoomAvailable?: (room: Room) => void }>
  = ({ onStateChange, onRoomAvailable }) => {
  const room = useRoomContext();

  useEffect(() => {
    if (!room) return;

    // Notify initial state
    onStateChange(room.state);
    if (onRoomAvailable && room.state === ConnectionState.Connected) {
      onRoomAvailable(room);
    }

    const handle = () => {
      onStateChange(room.state);
      if (onRoomAvailable && room.state === ConnectionState.Connected) {
        onRoomAvailable(room);
      }
    };
    room.on('connectionStateChanged', handle);
    return () => {
      room.off('connectionStateChanged', handle);
    };
  }, [room, onStateChange, onRoomAvailable]);

  return null;
};

const VoiceControls: React.FC<{
  room: Room | undefined;
  enableVoice: boolean;
}> = ({ room, enableVoice }) => {
  const [isMuted, setIsMuted] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const participants = useParticipants();
  
  useEffect(() => {
    if (room) {
      setIsConnected(room.state === ConnectionState.Connected);
      
      const handleStateChange = () => {
        setIsConnected(room.state === ConnectionState.Connected);
      };
      
      room.on('connectionStateChanged', handleStateChange);
      return () => {
        room.off('connectionStateChanged', handleStateChange);
      };
    }
  }, [room]);

  const toggleMute = useCallback(async () => {
    if (room && room.localParticipant) {
      await room.localParticipant.setMicrophoneEnabled(isMuted);
      setIsMuted(!isMuted);
    }
  }, [room, isMuted]);

  const leaveRoom = useCallback(() => {
    if (room) {
      room.disconnect();
    }
  }, [room]);

  if (!enableVoice) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Phone className="h-5 w-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Voice Chat</h3>
        </div>
        <div className="flex items-center space-x-2">
          <Users className="h-4 w-4 text-gray-600" />
          <span className="text-sm text-gray-600">{participants.length} participants</span>
        </div>
      </div>

      {/* Participants */}
      <div className="mb-4">
        <div className="space-y-2">
          {participants.map((participant) => (
            <div
              key={participant.identity}
              className="flex items-center space-x-3 p-2 bg-gray-50 rounded-md"
            >
              <div className="h-8 w-8 bg-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {participant.name?.[0] || participant.identity[0]}
                </span>
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">
                  {participant.name || participant.identity}
                </div>
                <div className="text-xs text-gray-500">
                  {participant.isSpeaking ? 'Speaking...' : 'Listening'}
                </div>
              </div>
              {participant.getTrackPublication(Track.Source.Microphone)?.isMuted ? (
                <MicOff className="h-4 w-4 text-red-500" />
              ) : (
                <Mic className="h-4 w-4 text-green-500" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="flex justify-center space-x-4">
        <button
          onClick={toggleMute}
          disabled={!isConnected}
          className={`p-3 rounded-full ${
            isMuted 
              ? 'bg-red-600 hover:bg-red-700' 
              : 'bg-green-600 hover:bg-green-700'
          } text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            isMuted ? 'focus:ring-red-500' : 'focus:ring-green-500'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </button>
        
        <button
          onClick={leaveRoom}
          className="p-3 rounded-full bg-red-600 hover:bg-red-700 text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          <PhoneOff className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

const LiveKitChatRoom: React.FC<LiveKitChatRoomProps> = ({
  roomName,
  token,
  wsUrl,
  onDisconnect,
  enableVoice = false,
  enableVideo = false,
  className = '',
}) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [room, setRoom] = useState<Room | undefined>();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionState, setConnectionState] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected');
  const [retryCount, setRetryCount] = useState(0);
  const [maxRetries] = useState(3);

  // Set connecting state when component mounts
  useEffect(() => {
    setConnectionState('connecting');
  }, []);

  const sendMessage = useCallback((message: string) => {
    if (!room) {
      logger.warn('Cannot send message: room is not connected');
      return;
    }
    
    if (!user) {
      logger.warn('Cannot send message: user is not authenticated');
      return;
    }

    const messageData = {
      type: 'chat_message',
      userId: user.uid,
      userName: (user as any).displayName || user.email || 'Anonymous',
      message,
      timestamp: new Date().toISOString(),
    };

    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(messageData));

    try {
      // Publish with explicit options object (compatible with latest SDK)
      if (room.localParticipant) {
        room.localParticipant.publishData(data, { reliable: true });
        logger.info('Sent chat message to room', { roomName, message });
      } else {
        logger.warn('Room localParticipant is not available');
        return;
      }
    } catch (err) {
      logger.error('Failed to publish chat message', err);
      return;
    }

    // Add to local messages immediately
    const newMessage: ChatMessage = {
      id: `${user.uid}-${Date.now()}`,
      userId: user.uid,
      userName: (user as any).displayName || user.email || 'You',
      message,
      timestamp: new Date(),
      type: 'user',
    };
    
    setMessages(prev => [...prev, newMessage]);
  }, [room, user, roomName]);

  // Expose simple console helpers for manual testing
  useEffect(() => {
    // @ts-ignore
    (window as any).sendLiveChat = (text: string) => {
      if (typeof text === 'string' && text.trim().length > 0) {
        sendMessage(String(text));
      }
    };
    // @ts-ignore
    (window as any).liveKitInfo = () => ({
      roomName: room?.name,
      state: room?.state,
      connected: room?.state === ConnectionState.Connected,
      hasLocal: !!room?.localParticipant,
    });
    return () => {
      // @ts-ignore
      delete (window as any).sendLiveChat;
      // @ts-ignore
      delete (window as any).liveKitInfo;
    };
  }, [sendMessage, room]);

  const handleRoomConnected = useCallback((room: Room | undefined) => {
    logger.info('handleRoomConnected called with room:', room?.name);
    
    // Check if room is valid immediately
    if (!room) {
      logger.error('Room is undefined in handleRoomConnected');
      setIsConnecting(false);
      setConnectionState('disconnected');
      return;
    }
    
    // Set room immediately to prevent it from being cleared
    setRoom(room);
    setIsConnecting(false);
    setConnectionState('connected');
    setRetryCount(0); // Reset retry count on successful connection
    logger.info('Connected to LiveKit room:', roomName);
    
    // Debug: Log room state after setting
    setTimeout(() => {
      logger.info('Room state after 1 second:', { 
        roomExists: !!room, 
        roomName: room?.name,
        connectionState: connectionState 
      });
    }, 1000);
      // Extra console tracing for debugging
      // eslint-disable-next-line no-console
      console.log('[LiveKit] onConnected → room:', roomName, 'name:', room?.name);
      // Expose for manual console testing (debug only)
      // @ts-ignore
      (window as any).__lk_room = room;
      
      // Add test function to window for debugging
      // @ts-ignore
      (window as any).testLiveKitConnection = () => {
        if (room) {
          logger.info('Testing LiveKit connection...');
          const testMessage = {
            type: 'debug_ping',
            message: 'test ping from frontend',
            timestamp: new Date().toISOString()
          };
          const encoder = new TextEncoder();
          const data = encoder.encode(JSON.stringify(testMessage));
          room.localParticipant?.publishData(data, { reliable: true });
          logger.info('Debug ping sent');
        } else {
          logger.warn('No room available for testing');
        }
      };
      
      // Add room disconnect handler to prevent undefined room errors
      room.on(RoomEvent.Disconnected, (reason) => {
        logger.info('Room disconnected:', reason);
        setRoom(undefined);
        setIsConnecting(false);
        setConnectionState('disconnected');
      });

      // Listen for data messages from the agent (use RoomEvent.DataReceived)
      room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
      try {
        const messageData = new TextDecoder().decode(payload);
        logger.info('Received data from agent:', { topic, messageData });
        // eslint-disable-next-line no-console
        console.log('[LiveKit] DataReceived ←', { topic, messageData });
        
        // Try to parse as JSON first
        try {
          const messageObj = JSON.parse(messageData);
          // Agent presence handshake
          if (messageObj.type === 'agent_online') {
            // eslint-disable-next-line no-console
            console.log('[LiveKit] agent_online received', messageObj);
            const agentMessage: ChatMessage = {
              id: `agent-online-${Date.now()}`,
              userId: 'agent',
              userName: messageObj.agentName || 'Rraasi AI',
              message: 'Agent connected. You can start chatting.',
              timestamp: new Date(),
              type: 'system',
            };
            setMessages(prev => [...prev, agentMessage]);
            return;
          }
          if (messageObj.type === 'debug_pong') {
            console.log('[LiveKit] debug_pong received', messageObj);
            const agentMessage: ChatMessage = {
              id: `agent-debug-${Date.now()}`,
              userId: 'agent',
              userName: messageObj.agentName || 'Rraasi AI',
              message: 'pong',
              timestamp: new Date(),
              type: 'system',
            };
            setMessages(prev => [...prev, agentMessage]);
            return;
          }
          if (messageObj.type === 'agent_response') {
            // eslint-disable-next-line no-console
            console.log('[LiveKit] agent_response received', messageObj);
            const agentMessage: ChatMessage = {
              id: `agent-${Date.now()}`,
              userId: 'agent',
              userName: messageObj.agentName || 'Rraasi AI',
              message: messageObj.message,
              timestamp: new Date(),
              type: 'system',
            };
            setMessages(prev => [...prev, agentMessage]);
          }
        } catch (jsonError) {
          // Handle plain text messages
          // eslint-disable-next-line no-console
          console.log('[LiveKit] plaintext received', messageData);
          const agentMessage: ChatMessage = {
            id: `agent-${Date.now()}`,
            userId: 'agent',
            userName: 'Rraasi AI',
            message: messageData,
            timestamp: new Date(),
            type: 'system',
          };
          setMessages(prev => [...prev, agentMessage]);
        }
      } catch (error) {
        logger.error('Error processing agent response:', error);
        // eslint-disable-next-line no-console
        console.error('[LiveKit] Error processing DataReceived', error);
      }
    });
  }, [roomName]);

  const handleRoomDisconnected = useCallback(() => {
    setRoom(undefined);
    setIsConnecting(false);
    setConnectionState('disconnected');
    logger.info('Disconnected from LiveKit room:', roomName);
    // eslint-disable-next-line no-console
    console.log('[LiveKit] onDisconnected ← room:', roomName);
    onDisconnect?.();
  }, [roomName, onDisconnect]);

  return (
    <div className={`h-full ${className}`}>
      {/* Connection Status Indicator */}
      <div className="flex items-center justify-between p-2 bg-gray-50 border-b">
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${
            connectionState === 'connected' ? 'bg-green-500' : 
            connectionState === 'connecting' ? 'bg-yellow-500' : 
            'bg-red-500'
          }`}></div>
          <span className="text-sm text-gray-600">
            {connectionState === 'connected' ? 'Connected' : 
             connectionState === 'connecting' ? 'Connecting...' : 
             'Disconnected'}
          </span>
        </div>
        {room && (
          <button
            onClick={() => {
              // @ts-ignore
              if (window.testLiveKitConnection) {
                // @ts-ignore
                window.testLiveKitConnection();
              }
            }}
            className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Test
          </button>
        )}
      </div>
      
      <LiveKitRoom
        video={enableVideo}
        audio={enableVoice}
        token={token}
        serverUrl={wsUrl}
        data-lk-theme="default"
        onConnected={() => {
          // Library does not pass the Room instance here; RoomBinder/ConnectionWatcher
          // will deliver the actual Room and authoritative state, but we can
          // optimistically mark as connected for user feedback.
          logger.info('LiveKitRoom onConnected fired');
          setIsConnecting(false);
          setConnectionState('connected');
        }}
        onDisconnected={handleRoomDisconnected}
        onError={(error) => {
          logger.error('LiveKit room error:', error);
          setConnectionState('disconnected');
          setIsConnecting(false);
          
          // Retry connection if we haven't exceeded max retries
          if (retryCount < maxRetries) {
            logger.info(`Retrying connection (${retryCount + 1}/${maxRetries})...`);
            setRetryCount(prev => prev + 1);
            setTimeout(() => {
              setConnectionState('connecting');
              setIsConnecting(true);
            }, 2000 * (retryCount + 1)); // Exponential backoff
          } else {
            logger.error('Max retry attempts reached. Connection failed.');
          }
        }}
        className="h-full"
      >
        {/* Bind to the internal Room instance exposed via context */}
        <RoomBinder onConnected={handleRoomConnected} />
        {/* Keep UI in sync with SDK state */}
        <ConnectionWatcher
          onStateChange={(state) => {
            if (state === ConnectionState.Connected) {
              setConnectionState('connected');
              setIsConnecting(false);
            } else if (state === ConnectionState.Connecting) {
              setConnectionState('connecting');
            } else {
              setConnectionState('disconnected');
            }
          }}
          onRoomAvailable={(r) => {
            // Ensure room state is set even if onConnected didn't provide it
            handleRoomConnected(r);
          }}
        />
        <div className="h-full flex flex-col lg:flex-row gap-4 p-4">
          {/* Chat Interface */}
          <div className="flex-1 min-h-0">
            <ChatInterface
              room={room}
              onSendMessage={sendMessage}
              messages={messages}
            />
          </div>
          
          {/* Voice Controls */}
          {enableVoice && (
            <div className="lg:w-80">
              <VoiceControls room={room} enableVoice={enableVoice} />
            </div>
          )}
        </div>
        
        {/* Audio Renderer */}
        <RoomAudioRenderer />
      </LiveKitRoom>
    </div>
  );
};

export default LiveKitChatRoom;
